/**
 * Users administration surface.
 *
 * - Admins (role === 'admin') and superadmins (is_superuser) can change a
 *   user's role and toggle their active state.
 * - Everyone else gets a read-only directory of users.
 *
 * Role/active changes are persisted via PATCH /users/<id>/.
 * @component
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import api, { MEDIA_URL } from '../../services/api';
import useCurrentUser from '../../utils/useCurrentUser';

const ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin' },
  { value: 'user', label: 'User' },
  { value: 'guest', label: 'Guest' },
];

const roleColor = (role) => {
  switch (role) {
    case 'admin':
      return 'primary';
    case 'guest':
      return 'warning';
    default:
      return 'default';
  }
};

const resolveImage = (image) => {
  if (!image) return undefined;
  return image.startsWith('http') ? image : `${MEDIA_URL}${image}`;
};

const initials = (user) => {
  const source = user.full_name || user.username || user.email || '';
  return source
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');
};

const UsersSettings = () => {
  const { user: me, canManageUsers, isSuperAdmin } = useCurrentUser();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    let active = true;
    api
      .get('users/')
      .then((res) => {
        if (active) setUsers(res.data);
      })
      .catch(() => {
        if (active) setError('Could not load users.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      [u.full_name, u.username, u.email, u.role]
        .filter(Boolean)
        .some((field) => field.toLowerCase().includes(q))
    );
  }, [users, search]);

  const patchUser = async (id, payload) => {
    setSavingId(id);
    const previous = users;
    // Optimistic update.
    setUsers((list) => list.map((u) => (u.id === id ? { ...u, ...payload } : u)));
    try {
      const res = await api.patch(`users/${id}/`, payload);
      setUsers((list) => list.map((u) => (u.id === id ? res.data : u)));
      setToast({ severity: 'success', message: 'User updated.' });
    } catch (err) {
      setUsers(previous); // rollback
      const detail =
        err?.response?.data?.detail || 'Failed to update user. Please try again.';
      setToast({ severity: 'error', message: detail });
    } finally {
      setSavingId(null);
    }
  };

  // A row is editable only by managers, and a non-superadmin cannot edit a
  // superadmin account (mirrors the backend guard).
  const canEditRow = (target) =>
    canManageUsers && (isSuperAdmin || !target.is_superuser);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h6" fontWeight={600}>
            Users
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {canManageUsers
              ? 'Manage roles and account access for your organisation.'
              : 'Directory of people in your organisation.'}
          </Typography>
        </Box>
        <TextField
          size="small"
          placeholder="Search users…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: { xs: '100%', sm: 280 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table sx={{ minWidth: 640 }}>
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 600, bgcolor: 'action.hover' } }}>
              <TableCell>User</TableCell>
              <TableCell>Email</TableCell>
              <TableCell width={160}>Role</TableCell>
              <TableCell width={120} align="center">
                Active
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((u) => {
              const editable = canEditRow(u);
              const isSelf = me && u.id === me.id;
              return (
                <TableRow key={u.id} hover>
                  <TableCell>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Avatar
                        src={resolveImage(u.image)}
                        sx={{ width: 36, height: 36, fontSize: 14 }}
                      >
                        {initials(u)}
                      </Avatar>
                      <Box>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <Typography variant="body2" fontWeight={600}>
                            {u.full_name || u.username}
                          </Typography>
                          {u.is_superuser && (
                            <Tooltip title="Superadmin">
                              <VerifiedUserIcon
                                color="primary"
                                sx={{ fontSize: 16 }}
                              />
                            </Tooltip>
                          )}
                          {isSelf && (
                            <Chip label="You" size="small" variant="outlined" />
                          )}
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          @{u.username}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {u.email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {editable ? (
                      <FormControl size="small" fullWidth>
                        <Select
                          value={u.role}
                          disabled={savingId === u.id}
                          onChange={(e) => patchUser(u.id, { role: e.target.value })}
                        >
                          {ROLE_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <Chip
                        size="small"
                        label={u.role}
                        color={roleColor(u.role)}
                        variant={u.role === 'user' ? 'outlined' : 'filled'}
                        sx={{ textTransform: 'capitalize' }}
                      />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {editable ? (
                      <Tooltip
                        title={
                          isSelf
                            ? 'You cannot deactivate your own account'
                            : u.is_active
                            ? 'Deactivate'
                            : 'Activate'
                        }
                      >
                        <span>
                          <Switch
                            size="small"
                            checked={u.is_active}
                            disabled={savingId === u.id || isSelf}
                            onChange={(e) =>
                              patchUser(u.id, { is_active: e.target.checked })
                            }
                          />
                        </span>
                      </Tooltip>
                    ) : (
                      <Chip
                        size="small"
                        label={u.is_active ? 'Active' : 'Inactive'}
                        color={u.is_active ? 'success' : 'default'}
                        variant="outlined"
                      />
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    align="center"
                    sx={{ py: 4 }}
                  >
                    No users match “{search}”.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast ? (
          <Alert
            severity={toast.severity}
            variant="filled"
            onClose={() => setToast(null)}
          >
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default UsersSettings;
